import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { API_URL, formatDate, getStatusColor, statusLabels } from "../../lib/utils";
import { FolderKanban, ArrowRight } from "lucide-react";

export const PortalProjectsPage = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetch_ = async () => {
            try {
                const res = await fetch(`${API_URL}/portal/projects`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) setProjects(await res.json());
            } catch (e) { console.error(e); }
            finally { setLoading(false); }
        };
        fetch_();
    }, [token]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="portal-projects-page">
            <div>
                <h1 className="font-heading font-bold text-2xl text-foreground">Mes projets</h1>
                <p className="text-muted-foreground text-sm mt-1">Suivez l'avancement de vos projets</p>
            </div>

            {projects.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center">
                        <FolderKanban className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">Aucun projet pour le moment</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {projects.map((p) => (
                        <Card
                            key={p.project_id}
                            className="cursor-pointer hover:border-primary/50 transition-colors"
                            onClick={() => navigate(`/portal/projects/${p.project_id}`)}
                            data-testid={`portal-project-card-${p.project_id}`}
                        >
                            <CardContent className="p-5">
                                <div className="flex items-start justify-between mb-3">
                                    <h3 className="font-medium text-base">{p.name}</h3>
                                    <Badge className={getStatusColor(p.status)}>
                                        {statusLabels[p.status] || p.status}
                                    </Badge>
                                </div>
                                {p.description && (
                                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{p.description}</p>
                                )}
                                <div className="flex items-center justify-between text-xs text-muted-foreground">
                                    <span>Créé le {formatDate(p.created_at)}</span>
                                    {p.end_date && <span>Échéance : {formatDate(p.end_date)}</span>}
                                </div>
                                <div className="flex items-center gap-2 mt-3">
                                    <ArrowRight className="w-4 h-4 text-primary" />
                                    <span className="text-sm text-primary font-medium">Voir le détail</span>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
};
